/**
 * AiChatSummaryCard — upload a WhatsApp chat export (.txt) and get a Hebrew
 * client summary from the nivChatSummary Cloud Function. Works for a lead
 * (target.kind = "prospect", id = phone) and a clinic ("clinic", id = clinicId).
 *
 * Props:
 *  - target: { kind: "prospect"|"clinic", id }
 *  - aiSummary: { text, generatedAt, sourceFileName, messagesCount, firstMessageAt, lastMessageAt } | null
 */

import { useRef, useState } from "react";
import { Sparkle, UploadSimple, Copy, ArrowsClockwise, ChatCircleText } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import { useToast } from "../../../../contexts/ToastContext";
import { useGenerateNivChatSummaryMutation } from "../../../../store/services/nivApi";
import { formatDate } from "./nivDerived";
import "./NivCrmCards.css";

const MAX_FILE_BYTES = 2 * 1024 * 1024;

const rtkErrorMessage = (error) =>
  error?.message || error?.data?.message || "אירעה שגיאה, נסה שוב";

function AiChatSummaryCard({ target, aiSummary }) {
  const { showSuccess, showError } = useToast();
  const inputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null); // file waiting for overwrite confirm
  const [generate, { isLoading }] = useGenerateNivChatSummaryMutation();

  const readAndGenerate = (file) => {
    const reader = new FileReader();
    reader.onerror = () => showError("לא ניתן לקרוא את הקובץ");
    reader.onload = async () => {
      try {
        const chatText = String(reader.result || "");
        if (!chatText.trim()) {
          showError("הקובץ ריק");
          return;
        }
        await generate({ target, chatText, fileName: file.name }).unwrap();
        showSuccess("הסיכום נוצר");
      } catch (error) {
        console.error("[CK-9803] Error generating AI chat summary:", {
          errorCode: "CK-9803",
          errorMessage: error?.message,
          targetKind: target?.kind,
          timestamp: new Date().toISOString(),
        });
        showError(rtkErrorMessage(error));
      }
    };
    reader.readAsText(file, "utf-8");
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!/\.txt$/i.test(file.name) && file.type && !file.type.startsWith("text/")) {
      showError("יש להעלות קובץ טקסט (.txt) שיוצא מוואטסאפ");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      showError("הקובץ גדול מדי (עד 2MB)");
      return;
    }
    if (aiSummary?.text) {
      setPendingFile(file);
      return;
    }
    readAndGenerate(file);
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    handleFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (isLoading) return;
    handleFile(e.dataTransfer?.files?.[0]);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(aiSummary?.text || "");
      showSuccess("הסיכום הועתק");
    } catch (error) {
      console.error("[CK-9804] Error copying AI summary:", {
        errorCode: "CK-9804",
        errorMessage: error?.message,
        timestamp: new Date().toISOString(),
      });
      showError("לא ניתן להעתיק");
    }
  };

  return (
    <section className="nlc-card nlc-card-ai">
      <header className="nlc-card-head">
        <span className="nlc-card-icon nlc-card-icon-ai">
          <Sparkle size={15} weight="fill" />
        </span>
        <div className="nlc-card-titles">
          <span className="nlc-card-eyebrow">AI</span>
          <h3 className="nlc-card-title">סיכום שיחת וואטסאפ</h3>
        </div>
        {aiSummary?.text && !isLoading && (
          <div className="nlc-head-actions">
            <button type="button" className="nlc-icon-btn" onClick={handleCopy} aria-label="העתק סיכום" title="העתק">
              <Copy size={14} />
            </button>
            <button
              type="button"
              className="nlc-icon-btn"
              onClick={() => inputRef.current?.click()}
              aria-label="העלה קובץ חדש"
              title="העלה קובץ חדש"
            >
              <ArrowsClockwise size={14} />
            </button>
          </div>
        )}
      </header>

      <input
        ref={inputRef}
        type="file"
        accept=".txt,text/plain"
        onChange={onInputChange}
        className="nlc-file-input"
        aria-hidden="true"
        tabIndex={-1}
      />

      {isLoading ? (
        <ContentLoader size="sm" compact message="לני קורא את השיחה…" />
      ) : aiSummary?.text ? (
        <>
          <div className="nlc-ai-meta">
            {aiSummary.sourceFileName && <span className="nlc-chip">{aiSummary.sourceFileName}</span>}
            {aiSummary.messagesCount != null && (
              <span className="nlc-chip">
                <ChatCircleText size={11} weight="fill" /> {aiSummary.messagesCount} הודעות
              </span>
            )}
            {aiSummary.firstMessageAt && aiSummary.lastMessageAt && (
              <span className="nlc-chip" dir="ltr">
                {aiSummary.firstMessageAt} → {aiSummary.lastMessageAt}
              </span>
            )}
            {aiSummary.generatedAt && (
              <span className="nlc-ai-generated">נוצר {formatDate(aiSummary.generatedAt)}</span>
            )}
          </div>
          <pre className="nlc-ai-text">{aiSummary.text}</pre>
        </>
      ) : (
        <button
          type="button"
          className="nlc-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
        >
          <UploadSimple size={22} />
          <strong>בחר קובץ chat.txt</strong>
          <span>ייצוא שיחה מוואטסאפ (ללא מדיה) — לני יכתוב סיכום לקוח מלא</span>
        </button>
      )}

      <ConfirmDialog
        isOpen={!!pendingFile}
        onClose={() => setPendingFile(null)}
        onConfirm={() => {
          const f = pendingFile;
          setPendingFile(null);
          readAndGenerate(f);
        }}
        title="החלפת הסיכום"
        message="כבר קיים סיכום AI ללקוח זה. להעלות קובץ חדש ולהחליף את הסיכום הקיים?"
        confirmText="החלף"
        type="warning"
        icon="warning"
      />
    </section>
  );
}

export default AiChatSummaryCard;
